import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  type AddressBookOrganization,
  type AddressBookLocation,
} from '@logistiek-zonder-papier/lzp-library'
import { axiosClient } from '@/utils/axios-client'
import type { UUID } from 'crypto'
import type {
  CreateAddressBookLocation,
  CreateAddressBookOrganization,
} from '@/utils/address-utils'

export const useAddressBookStore = defineStore('addressBookStore', () => {
  const addressBookOrganisations = ref<AddressBookOrganization[]>([])
  const addressBookOrganisation = ref<AddressBookOrganization | null>(null)
  const addressBookLocations = ref<AddressBookLocation[]>([])
  const addressBookLocation = ref<AddressBookLocation | null>(null)

  async function fetchAddressBooks() {
    const response = await axiosClient.request<AddressBookOrganization[]>({
      url: '/address-books/total',
      method: 'GET',
    })

    addressBookOrganisations.value = response.data
    return addressBookOrganisations.value
  }

  async function fetchAddressBook(id: UUID) {
    const response = await axiosClient.request<AddressBookOrganization>({
      url: `/address-books/${id}`,
      method: 'GET',
    })

    addressBookOrganisation.value = response.data
    return addressBookOrganisation.value
  }

  async function createAddressBookOrganization(data: CreateAddressBookOrganization) {
    const response = await axiosClient.request<AddressBookOrganization>({
      url: 'address-books',
      method: 'POST',
      data,
    })

    addressBookOrganisations.value.push(response.data)
  }

  async function updateAddressBookOrganization(data: Partial<AddressBookOrganization>) {
    await axiosClient.request({
      url: `address-books/${data.id}`,
      method: 'PATCH',
      data: data,
    })
  }

  async function deleteAddressBookOrganization(id: UUID) {
    await axiosClient.request({
      url: `address-books/${id}`,
      method: 'DELETE',
    })

    addressBookOrganisations.value = addressBookOrganisations.value.filter(
      address => address.id !== id
    )
  }

  async function fetchAddressBookLocations(organisationId: UUID) {
    const response = await axiosClient.request({
      url: `address-books/${organisationId}/locations`,
      method: 'GET',
    })

    addressBookLocations.value = response.data
  }

  async function fetchAddressBookLocation(organisationId: UUID, locationId: UUID) {
    const response = await axiosClient.request({
      url: `address-books/${organisationId}/locations/${locationId}`,
      method: 'GET',
    })

    addressBookLocation.value = response.data
  }

  async function createAddressBookLocation(organisationId: UUID, data: CreateAddressBookLocation) {
    await axiosClient.request({
      url: `address-books/${organisationId}/locations`,
      method: 'POST',
      data: { ...data, organizationId: organisationId },
    })
    await fetchAddressBookLocations(organisationId)
  }

  async function updateAddressBookLocation(
    organisationId: UUID,
    locationId: UUID,
    data: Partial<AddressBookLocation>
  ) {
    await axiosClient.request({
      url: `address-books/${organisationId}/locations/${locationId}`,
      method: 'PATCH',
      data: data,
    })
  }

  async function deleteAddressBookLocation(organisationId: UUID, locationId: UUID) {
    await axiosClient.request({
      url: `address-books/${organisationId}/locations/${locationId}`,
      method: 'DELETE',
    })

    addressBookLocations.value = addressBookLocations.value.filter(
      location => location.id !== locationId
    )
  }

  return {
    addressBookOrganisation,
    addressBookOrganisations,
    fetchAddressBook,
    fetchAddressBooks,
    createAddressBookOrganization,
    updateAddressBookOrganization,
    deleteAddressBookOrganization,
    addressBookLocations,
    addressBookLocation,
    fetchAddressBookLocations,
    fetchAddressBookLocation,
    createAddressBookLocation,
    updateAddressBookLocation,
    deleteAddressBookLocation,
  }
})
